import { useState, useEffect, useMemo } from 'react'
import { Info, ChevronDown } from 'lucide-react'
import useAudienceTargetingStore from '@/store/audienceTargetingStore'
import { Checkbox } from '@/components/ui/checkbox'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip.jsx'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import MaleIcon from '@/components/icons/MaleIcon'
import FemaleIcon from '@/components/icons/FemaleIcon'
import GenderIcon from '@/components/icons/GenderIcon'
import TableRowSkeleton from '@/components/common/TableRowSkeleton'
import MetricsPicker from '../MetricsPicker'
import { useColumnPreferences } from '../hooks/useColumnPreferences'

const Row = ({ gender, isLast, checked, handleGenderToggle, selectedColumns, genderMetrics, formatMetricValue }) => {
    const Icon = gender.icon
    let style = {}

    if (isLast) style.borderBottom = '0px solid transparent'

    // Helper function to get metric value from genderMetrics
    const getMetricValue = (apiKey) => {
        const metrics = genderMetrics[gender.id] || {}
        return metrics[apiKey] || 0
    }

    return (
        <TableRow
            style={style}
            className="tw:cursor-pointer hover:tw:bg-gray-700/20"
            onClick={() => handleGenderToggle(gender.id)}
        >
            <TableCell className="tw:pl-3">
                <div className="tw:flex tw:items-center tw:gap-3">
                    <Checkbox
                        checked={checked}
                        onCheckedChange={() => handleGenderToggle(gender.id)}
                        onClick={(e) => e.stopPropagation()}
                    />
                    <div className="tw:flex tw:gap-0.5">
                        <Icon className="tw:w-5 tw:h-5 tw:text-[#899CBD]" />
                        <span className="tw:text-sm tw:text-white">{gender.label}</span>
                    </div>
                </div>
            </TableCell>
            {/* Dynamic metric columns */}
            {selectedColumns.map((column) => {
                const value = getMetricValue(column.apiKey)
                const formattedValue = formatMetricValue(value, column)

                return (
                    <TableCell key={column.id} className="tw:text-right tw:text-sm tw:text-white tw:pr-3">
                        {formattedValue}
                    </TableCell>
                )
            })}
        </TableRow>
    )
}

const GenderSettings = ({
    stage,
    audience,
    metricsDataLoading,
    formatMetricValue,
    metricsData,
    setStepContentData,
    getStepContentData,
}) => {
    const { getAudienceSettings, updateAudienceSetting } = useAudienceTargetingStore()
    const [selectedGenders, setSelectedGenders] = useState([])

    // Column preferences hook
    const {
        selectedColumns,
        allMetrics,
        updateSelectedColumns,
        isLoading: allMetricsLoading,
    } = useColumnPreferences('Gender', setStepContentData, getStepContentData)

    // Handle metric replacement for a specific column index
    const handleMetricReplace = (columnIndex, newMetric) => {
        const currentMetrics = [...selectedColumns]
        currentMetrics[columnIndex] = newMetric
        updateSelectedColumns(currentMetrics)
    }

    // Process metrics data to organize by gender and metric
    const genderMetrics = useMemo(() => {
        const metrics = {}
        if (metricsData) {
            metricsData.forEach((metric) => {
                const gender = metric.breakdownValue.toLowerCase()
                if (!metrics[gender]) {
                    metrics[gender] = {}
                }
                // Use latest value for other metrics (rates, percentages, etc.)
                metrics[gender][metric.metricApiKey] = metric.value
            })
        }
        return metrics
    }, [metricsData])

    // Gender options with display data
    const genderOptions = [
        {
            id: 'male',
            label: 'Male',
            icon: MaleIcon,
        },
        {
            id: 'female',
            label: 'Female',
            icon: FemaleIcon,
        },
    ]

    // Gender options (no sorting)
    const displayGenderOptions = genderOptions

    // Get current settings
    const audienceSettings = getAudienceSettings(stage, audience)
    const storedGenders = audienceSettings?.gender_targets

    // Initialize from stored value
    useEffect(() => {
        if (storedGenders && Array.isArray(storedGenders)) {
            setSelectedGenders(storedGenders)
        } else {
            // Default to both genders selected
            const defaultGenders = ['male', 'female']
            setSelectedGenders(defaultGenders)
            updateAudienceSetting(stage, audience, 'gender_targets', defaultGenders)
        }
    }, [storedGenders, stage, audience, updateAudienceSetting])

    const handleGenderToggle = (genderId) => {
        let newSelection
        if (selectedGenders.includes(genderId)) {
            // Don't allow deselecting if it's the last one selected
            if (selectedGenders.length === 1) {
                return
            }
            newSelection = selectedGenders.filter((g) => g !== genderId)
        } else {
            newSelection = [...selectedGenders, genderId]
        }

        setSelectedGenders(newSelection)
        updateAudienceSetting(stage, audience, 'gender_targets', newSelection)
    }

    return (
        <div className="tw:flex tw:gap-6 tw:flex-col tw:bg-secondary-background tw:rounded-xs">
            <div className="tw:flex tw:gap-1.5 tw:items-center tw:p-3 tw:pb-0">
                <span className="tw:text-gray-200">
                    <GenderIcon className="tw:w-6 tw:h-6" />
                </span>
                <span className="tw:font-semibold">Gender</span>
                <Tooltip>
                    <TooltipTrigger>
                        <Info className="tw:w-4 tw:h-4 tw:text-gray-200" />
                    </TooltipTrigger>
                    <TooltipContent showArrow={false}>
                        <div className="tw:text-sm tw:text-gray-400">
                            Target your ads based on gender demographics.
                            <br />
                            <span className="tw:text-white tw:font-medium tw:block tw:mt-2">
                                At least one gender must be selected
                            </span>
                        </div>
                    </TooltipContent>
                </Tooltip>
            </div>

            <div className="">
                <Table className="tw:[&_tbody_tr:nth-child(even)]:bg-[#142234]">
                    <TableHeader>
                        <TableRow>
                            <TableHead className="tw:text-[#899CBD] tw:pl-3">Gender</TableHead>
                            {selectedColumns.map((column, index) => (
                                <TableHead key={column.id} className="tw:text-[#899CBD] tw:!text-right tw:pr-3">
                                    <MetricsPicker
                                        allMetrics={allMetrics}
                                        currentMetric={column}
                                        onMetricSelect={(metric) => handleMetricReplace(index, metric)}
                                    >
                                        <button className="tw:flex tw:items-center tw:justify-end tw:gap-1  hover:tw:text-white tw:transition-colors tw:cursor-pointer tw:w-full">
                                            <span>{column.name}</span>
                                            <ChevronDown className="tw:w-4 tw:h-4" />
                                        </button>
                                    </MetricsPicker>
                                </TableHead>
                            ))}
                        </TableRow>
                    </TableHeader>
                    {metricsDataLoading || allMetricsLoading ? (
                        <TableRowSkeleton
                            rows={3}
                            showIcon={true}
                            showCheckbox={true}
                            columns={selectedColumns.length + 1}
                        />
                    ) : (
                        <TableBody>
                            {displayGenderOptions.map((gender, index) => (
                                <Row
                                    key={gender.id}
                                    gender={gender}
                                    handleGenderToggle={handleGenderToggle}
                                    isLast={displayGenderOptions.length - 1 === index}
                                    checked={selectedGenders.includes(gender.id)}
                                    selectedColumns={selectedColumns}
                                    genderMetrics={genderMetrics}
                                    formatMetricValue={formatMetricValue}
                                />
                            ))}
                        </TableBody>
                    )}
                </Table>
            </div>
        </div>
    )
}

export default GenderSettings
